import { Card, Collapse } from "antd";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useState, useEffect } from "react";
import { CaretRightOutlined } from "@ant-design/icons";

const { Panel } = Collapse;

const Terms = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  // Last Updated Date Management
  const LAST_UPDATED = t("last_updated_date") || "November 27, 2025";

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Table of Contents data
  const tocSections = [
    { id: "section-1", title: t("toc_what_is_quto") },
    { id: "section-2", title: t("toc_information_collected") },
    { id: "section-3", title: t("toc_payments") },
    { id: "section-4", title: t("toc_ai_tools") },
    { id: "section-5", title: t("toc_local_tools") },
    { id: "section-6", title: t("toc_what_not_to_do") },
    { id: "section-7", title: t("toc_our_responsibility") },
    { id: "section-8", title: t("toc_changes_to_terms") },
    { id: "section-9", title: t("toc_contact_us") }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-8 text-gray-800">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-indigo-700 mb-2">
            {t("terms_conditions_title")}
          </h1>
          <p className="text-lg text-gray-600">
            {t("last_updated")}: {LAST_UPDATED}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile: Collapsible Table of Contents */}
          <div className="lg:hidden">
            <Collapse 
              bordered={false}
              expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
              className="bg-white"
            >
              <Panel header={t("table_of_contents")} key="1" className="font-semibold">
                <div className="space-y-2">
                  {tocSections.map((section, index) => (
                    <a 
                      key={section.id}
                      href={`#${section.id}`}
                      className="block text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg px-3 py-2 transition-colors duration-200"
                    >
                      <span className="font-medium">{index + 1}.</span> {section.title}
                    </a>
                  ))}
                </div>
              </Panel>
            </Collapse>
          </div>

          {/* Desktop: Sidebar Table of Contents - Sticky */}
          <div className="hidden lg:block lg:w-1/4">
            <Card className="sticky top-24 bg-indigo-50 border-indigo-200 shadow-sm">
              <h3 className="text-lg font-semibold text-indigo-700 mb-4">{t("table_of_contents")}</h3>
              <div className="space-y-2">
                {tocSections.map((section, index) => (
                  <a 
                    key={section.id}
                    href={`#${section.id}`}
                    className="block text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-lg px-3 py-2 transition-colors duration-200"
                  >
                    <span className="font-medium">{index + 1}.</span> {section.title}
                  </a>
                ))}
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            <Card className="shadow-lg rounded-xl">
              <div className="prose max-w-none">
                {/* Welcome Section */}
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">{t("welcome_to_quto")}</h2>
                  <p className="mb-2">
                    {t("terms_intro_1")}
                  </p>
                  <p>
                    {t("terms_intro_2")}
                  </p>
                </div>

                <h2 id="section-1" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  1. {t("what_quto_is")}
                </h2>
                <p className="mb-4">
                  {t("what_quto_is_description")}
                </p>
                <ul className="list-disc list-inside mb-4 ml-4">
                  <li>{t("api_testing")}</li>
                  <li>{t("json_tools")}</li>
                  <li>{t("code_utilities")}</li>
                  <li>{t("ai_powered_tools")}</li>
                  <li>{t("formatters_and_generators")}</li>
                </ul>
                <p className="mb-8">
                  {t("free_and_paid_tools")}
                </p>

                <h2 id="section-2" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  2. {t("information_we_collect")}
                </h2>
                <p className="mb-3">{t("we_collect")}:</p>
                <ul className="list-disc list-inside mb-3 ml-4">
                  <li>{t("your_name")}</li>
                  <li>{t("your_email_address")}</li>
                  <li>{t("payment_details_info")}</li>
                </ul>
                <p className="mb-3">{t("we_use_information_to")}:</p>
                <ul className="list-disc list-inside mb-8 ml-4">
                  <li>{t("provide_services")}</li>
                  <li>{t("send_receipts_updates")}</li>
                  <li>{t("offer_customer_support")}</li>
                </ul>

                <h2 id="section-3" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  3. {t("payments")}
                </h2>
                <p className="mb-3">
                  {t("some_tools_cost_money")}
                </p>
                <ul className="list-disc list-inside mb-8 ml-4">
                  <li>{t("payments_processed_by_partners")}</li>
                  <li>{t("we_do_not_store_card")}</li>
                  <li>{t("refunds_policy")}</li>
                </ul>

                <h2 id="section-4" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  4. {t("ai_tools")}
                </h2>
                <p className="mb-3">
                  {t("ai_tools_description")}
                </p>
                <ul className="list-disc list-inside mb-8 ml-4">
                  <li>{t("ai_data_sent")}</li>
                  <li>{t("ai_may_not_be_correct")}</li>
                  <li>{t("check_results_responsibly")}</li>
                  <li>{t("not_responsible_for_ai_mistakes")}</li>
                </ul>

                <h2 id="section-5" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  5. {t("local_tools")}
                </h2>
                <p className="mb-3">
                  {t("local_tools_description")}
                </p>
                <p className="mb-3">{t("this_means")}:</p>
                <ul className="list-disc list-inside mb-8 ml-4">
                  <li>{t("data_stays_on_device")}</li>
                  <li>{t("we_do_not_see_data")}</li>
                </ul>

                <h2 id="section-6" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  6. {t("what_you_should_not_do")}
                </h2>
                <p className="mb-3">{t("please_do_not")}:</p>
                <ul className="list-disc list-inside mb-3 ml-4">
                  <li>{t("use_for_illegal_activities")}</li>
                  <li>{t("hack_or_misuse")}</li>
                  <li>{t("copy_or_clone")}</li>
                  <li>{t("use_ai_for_harmful_content")}</li>
                </ul>
                <p className="mb-8">
                  {t("break_rules_consequence")}
                </p>

                <h2 id="section-7" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  7. {t("our_responsibility")}
                </h2>
                <p className="mb-3">
                  {t("we_try_our_best")}
                </p>
                <ul className="list-disc list-inside mb-3 ml-4">
                  <li>{t("cannot_promise_always_online")}</li>
                  <li>{t("not_responsible_for_data_loss")}</li>
                  <li>{t("not_responsible_for_ai_outputs")}</li>
                  <li>{t("use_at_your_own_risk")}</li>
                </ul>

                <h2 id="section-8" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  8. {t("changes_to_terms")}
                </h2>
                <p className="mb-3">
                  {t("we_may_update_terms")}
                </p>
                <p className="mb-3">
                  {t("we_will_update_date")}
                </p>
                <p className="mb-8">
                  {t("using_after_changes_acceptance")}
                </p>

                <h2 id="section-9" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  9. {t("contact_us")}
                </h2>
                <p className="mb-2">{t("if_you_have_questions")}:</p>
                <p className="text-indigo-600 font-medium">
                  <strong>{t("email")}:</strong> support@quto.dev<br />
                  <strong>{t("website")}:</strong> https://quto.dev
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Terms;